import {
    RUNNING_INTENSITY_ZONES,
    RUNNING_SEGMENT_TYPES,
    getRunningLabel,
} from "./constants";

const getGroupKey = (group = {}, index = 0) => group.id || group.value || `group_${index}`;

const getZoneLabel = (zoneValue) => {
    const zone = RUNNING_INTENSITY_ZONES.find(item => item.value === zoneValue);
    return zone ? `${zone.label} - ${zone.desc}` : zoneValue || "";
};

const getSegmentName = (segment = {}) =>
    segment.label || getRunningLabel(RUNNING_SEGMENT_TYPES, segment.segmentType);

const resolvePaceTarget = (segment = {}, paceGroup = null, groupIndex = 0) => {
    if (segment.targetType !== "pace") return null;

    if (paceGroup) {
        const groupKey = getGroupKey(paceGroup, groupIndex);
        const groupTarget = segment.paceGroupTargetsById?.[groupKey];
        if (groupTarget?.targetPaceMin || groupTarget?.targetPaceMax) {
            return {
                type: "pace",
                min: groupTarget.targetPaceMin || "",
                max: groupTarget.targetPaceMax || "",
                source: "pace_group_segment",
            };
        }

        const fallbackPace = segment.segmentType === "interval" || segment.segmentType === "hill" || segment.segmentType === "stride"
            ? paceGroup.intervalPace
            : segment.effortZone === "z4"
                ? paceGroup.thresholdPace
                : segment.effortZone === "z1" || segment.effortZone === "z2"
                    ? paceGroup.easyPace || paceGroup.longRunPace
                    : paceGroup.thresholdPace || paceGroup.easyPace;

        if (fallbackPace) {
            const [min = "", max = ""] = String(fallbackPace).split("-").map(item => item.trim());
            return {
                type: "pace",
                min,
                max,
                source: "pace_group_default",
            };
        }
    }

    if (segment.targetPaceMin || segment.targetPaceMax) {
        return {
            type: "pace",
            min: segment.targetPaceMin || "",
            max: segment.targetPaceMax || "",
            source: "segment",
        };
    }

    return null;
};

const buildTarget = (segment = {}, paceGroup = null, groupIndex = 0) => {
    if (segment.targetType === "pace") {
        return resolvePaceTarget(segment, paceGroup, groupIndex) || {
            type: "effort",
            zone: segment.effortZone || "",
            label: getZoneLabel(segment.effortZone),
        };
    }

    if (segment.targetType === "heart_rate") {
        return { type: "heart_rate", zone: segment.heartRateZone || "", label: segment.heartRateZone || "" };
    }

    if (segment.targetType === "power") {
        return { type: "power", zone: segment.powerZone || "", label: segment.powerZone || "" };
    }

    if (segment.targetType === "cadence") {
        return { type: "cadence", value: segment.cadenceTarget || "", label: segment.cadenceTarget || "" };
    }

    if (segment.targetType === "effort") {
        return { type: "effort", zone: segment.effortZone || "", label: getZoneLabel(segment.effortZone) };
    }

    return { type: "none" };
};

const buildDuration = (segment = {}) => {
    if (segment.durationType === "distance") {
        return {
            type: "distance",
            value: Number(segment.distance) || 0,
            unit: segment.distanceUnit || "km",
        };
    }

    if (segment.durationType === "time") {
        return {
            type: "time",
            value: Number(segment.duration) || 0,
            unit: "min",
        };
    }

    return {
        type: segment.durationType || "open",
        value: null,
        unit: "",
    };
};

const buildStep = ({ segment, block, blockIndex, segmentIndex, repeatIndex, paceGroup, groupIndex }) => ({
    id: `b${blockIndex + 1}_r${repeatIndex + 1}_s${segmentIndex + 1}`,
    name: getSegmentName(segment),
    stepType: segment.segmentType || "run",
    blockName: block.name || `Bloco ${blockIndex + 1}`,
    repeatIndex: repeatIndex + 1,
    duration: buildDuration(segment),
    target: buildTarget(segment, paceGroup, groupIndex),
    intensityZone: segment.effortZone || "",
    intensityLabel: getZoneLabel(segment.effortZone),
    terrain: segment.terrain || block.terrain || "",
    restAfter: segment.restAfter || "",
    notes: segment.notes || "",
});

export const buildRunningWatchExport = ({ workout = {}, paceGroup = null, groupIndex = 0 }) => {
    const blocks = workout.blocks || [];
    const clubSession = workout.clubSession || {};
    const steps = [];

    blocks.forEach((block, blockIndex) => {
        const repeat = Math.max(1, Number(block.repeat || 1));

        Array.from({ length: repeat }).forEach((_, repeatIndex) => {
            (block.sets || []).forEach((segment, segmentIndex) => {
                steps.push(buildStep({
                    segment,
                    block,
                    blockIndex,
                    segmentIndex,
                    repeatIndex,
                    paceGroup,
                    groupIndex,
                }));
            });
        });
    });

    return {
        schema: "neoapp.running.watch_workout.v1",
        exportedAt: new Date().toISOString(),
        workoutName: workout.name || "Treino de corrida",
        modality: "running",
        targetDevice: "generic_watch",
        paceGroup: paceGroup ? {
            id: getGroupKey(paceGroup, groupIndex),
            label: paceGroup.label || "",
            description: paceGroup.desc || "",
        } : null,
        club: clubSession?.isClubSession ? {
            teamName: clubSession.teamName || "",
            coachName: clubSession.coachName || "",
            startTime: clubSession.startTime || "",
            meetingPoint: clubSession.meetingPoint || "",
            routeName: clubSession.routeName || "",
            safetyNotes: clubSession.safetyNotes || "",
        } : null,
        cyclePlan: workout.cyclePlan?.enabled ? workout.cyclePlan : null,
        attendancePlan: workout.attendancePlan?.enabled ? workout.attendancePlan : null,
        steps,
        notes: [
            "Exportacao generica inicial do NeoApp.",
            "Este JSON e a base para conversao futura em formatos Garmin/FIT/TCX ou integracao por API.",
        ],
    };
};

export const downloadRunningWatchExport = (payload) => {
    const safeName = (payload.workoutName || "treino-corrida")
        .toLowerCase()
        .normalize("NFD")
        .replace(/[\u0300-\u036f]/g, "")
        .replace(/[^a-z0-9]+/g, "-")
        .replace(/(^-|-$)/g, "");
    const groupSuffix = payload.paceGroup?.label ? `-${payload.paceGroup.label.toLowerCase()}` : "";
    const blob = new Blob([JSON.stringify(payload, null, 2)], { type: "application/json;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `${safeName}${groupSuffix}-watch.json`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
};
